import { useState, useEffect, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, History, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ResponsiveLayoutProps {
  // Left panel (thread list/contacts)
  leftPanel: ReactNode;
  // Right panel (chat content)
  rightPanel: ReactNode;
  // Whether the left panel should be shown
  showLeftPanel: boolean;
  // Callback for mobile back navigation
  onBack?: () => void;
  // Whether we're in chat view (for mobile)
  isInChatView?: boolean;
  // Callback when mobile drawer should close (e.g., thread selected)
  onMobileDrawerClose?: () => void;
}

export const ResponsiveLayout = ({
  leftPanel,
  rightPanel,
  showLeftPanel,
  onBack,
  isInChatView = false,
  onMobileDrawerClose,
}: ResponsiveLayoutProps) => {
  // Track whether sidebar is collapsed on desktop
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Track whether sidebar is open on mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Track screen size
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close mobile menu when switching to desktop or escape key
  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobile]);

  // Mobile layout (single column)
  if (isMobile) {
    return (
      <div className="relative h-full">
        {/* Mobile Menu Button - Show when in chat view */}
        {isInChatView && (
          <div className="absolute top-2 left-2 z-20">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="h-8 w-8 rounded-full bg-white/70 shadow-sm dark:bg-white/5"
            >
              {isMobileMenuOpen ? <X className="h-4 w-4" /> : <History className="h-4 w-4" />}
            </Button>
          </div>
        )}

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 z-30"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Sidebar */}
            <div className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-background z-40 shadow-2xl overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Threads</h2>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {/* Wrap in div that notifies parent when content is clicked */}
                <div onClick={() => {
                  // Close mobile menu when thread is selected
                  setIsMobileMenuOpen(false);
                  onMobileDrawerClose?.();
                }}>
                  {leftPanel}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main Content */}
        <div className="h-full">
          {showLeftPanel && !isInChatView ? leftPanel : rightPanel}
        </div>
      </div>
    );
  }

  // Desktop layout (two columns)
  return (
    <div className="flex gap-4 h-full">
      {/* Left Panel (Thread List) */}
      {showLeftPanel && (
        <div
          className={`transition-all duration-300 ease-in-out ${
            isCollapsed ? "w-14" : "w-80 lg:w-96"
          } flex-shrink-0 relative`}
        >
          {/* Collapse Toggle Button */}
          <div className="absolute -right-3 top-1/2 -translate-y-1/2 z-10">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-6 w-6 rounded-full border border-border/60 bg-white/70 shadow-sm hover:bg-white dark:bg-white/5"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Panel Content */}
          <div className={`h-full overflow-hidden ${isCollapsed ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
            {leftPanel}
          </div>

          {/* Collapsed State Icon */}
          {isCollapsed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-10 text-muted-foreground">
                <History className="h-5 w-5" />
                <span className="text-xs rotate-90 whitespace-nowrap">Threads history</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Right Panel (Chat) */}
      <div className="flex-1 min-w-0">
        {rightPanel}
      </div>
    </div>
  );
};
